import { lazy, Suspense, useCallback, useEffect } from 'react';
import { Center, Flex, Group, Stack } from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { BasicButton, Section, SectionText, SectionTitle, TextAreaFilled, TextFieldFilled } from 'components';
import classes from './index.module.css';
import { AppConstants, ServiceConstants } from '../../constants';
import { PublicService } from 'services';
import { IRootState } from 'config/redux';
import { AppUtils } from 'utils';
import { useMask } from '@react-input/mask';
const Map = lazy(() => import('components/map'));

type ContactForm = {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: string;
  message: string;
}

const { MASK_SWISS_PHONE_NUMBER, PHONE_NUMBER_CODES, PHONE_NUMBER_DATA } = AppConstants;
const { REQUEST_CONTACT_ID } = ServiceConstants;

const Contact = () => {
  const phoneNumberRef = useMask({
    mask: MASK_SWISS_PHONE_NUMBER,
    replacement: { _: /\d/ },
  });
  const { t } = useTranslation();
  const contactServiceObject = useSelector((state: IRootState) => state.service.object[REQUEST_CONTACT_ID]);

  const form = useForm({
    initialValues: {
      firstName: '',
      lastName: '',
      email: '',
      phoneNumber: '',
      message: ''
    } as ContactForm,
    validate: {
      firstName: (value) => (!value || value.trim().length === 0 ? t('input.required') : null),
      lastName: (value) => (!value || value.trim().length === 0 ? t('input.required') : null),
      email: (value) => {
        if (!value || value.trim().length === 0) {
          return t('input.required')
        }
        if (!/^\S+@\S+\.\S+$/.test(value)) {
          return t('input.email.invalid')
        }
        return null;
      },
      phoneNumber: (value) => {
        if (!value || value.trim().length === 0) {
          return t('input.required')
        }
        if (!PHONE_NUMBER_CODES.some(code => value.startsWith(code) && PHONE_NUMBER_DATA[code]?.length.includes(value.replace(/\s/g, '').length))) {
          return t('input.phoneNumber.invalid');
        }
        return null;
      },
      message: (value) => (value.length < 1 ? t('input.required') : null),
    },
  });

  useEffect(() => {
    AppUtils.resolveServiceObject(
      contactServiceObject,
      REQUEST_CONTACT_ID,
      () => {
        form.reset();
        notifications.show({
          title: t('contact.success.title'),
          message: t('contact.success.description'),
          color: 'green',
        });
      },
    );
  }, [contactServiceObject]);

  const submitContactForm = useCallback(async (values: ContactForm) => {
    PublicService.sendEmail(values);
  }, []);

  return (
    <Section id='contact'>
      <Flex direction={{ base: 'column', md: 'row' }} gap={{ base: 40, md: 80 }}>
        <Stack className={classes.formBox}>
          <Stack gap={12}>
            <SectionTitle title={t('contact.title')} variant='dark' />
            <SectionText text={t('contact.description')} variant='light' />
          </Stack>
          <form onSubmit={form.onSubmit((values) => submitContactForm(values))} className={classes.form}>
            <Stack gap={12}>
              <Group gap='xs' align='flex-start' grow>
                <TextFieldFilled {...form.getInputProps('firstName')} placeholder={t('contact.form.firstname')} />
                <TextFieldFilled {...form.getInputProps('lastName')} placeholder={t('contact.form.lastname')} />
              </Group>
              <TextFieldFilled {...form.getInputProps('email')} placeholder={t('contact.form.email')} />
              <TextFieldFilled {...form.getInputProps('phoneNumber')} placeholder={t('input.phoneNumber.placeholder')} ref={phoneNumberRef} />
              <TextAreaFilled {...form.getInputProps('message')} placeholder={t('contact.form.message')} />
            </Stack>
            <BasicButton mt={24} type='submit'>{t('contact.form.cta')}</BasicButton>
          </form>
        </Stack>
        <Center className={classes.map}>
          <Suspense fallback={null}>
            <Map />
          </Suspense>
        </Center>
      </Flex>
    </Section>
  )
}

export default Contact;